import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { enumFieldType, fieldTypes } from "../../constant/WebToLead";
import PickList from "./PickList";

const CreateEditCustomField = ({
  closeEdit,
  onCreate,
  initialValues,
  customFieldsLength,
  existingFieldNames,
}) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [isLimitExceed, setLimitExceed] = useState(false);
  const [error, setError] = useState();
  const [type, setType] = useState();
  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
    closeEdit();
    setType();
    form.resetFields();
  };
  const handleCancel = () => {
    setLimitExceed(false);
  };

  useEffect(() => {
    if (initialValues?.fieldName) {
      setType(initialValues?.fieldType);
      showDrawer();
    }
    form.setFieldsValue({
      ...initialValues,
    });
  }, [initialValues, form]);

  const onFinish = (values) => {
    if (
      [enumFieldType.PICK_LIST, enumFieldType.MULTI_PICK_LIST].includes(
        values?.fieldType
      ) &&
      !values?.pickValues?.length
    ) {
      setError("PickList Values is required");
    } else {
      onCreate({
        ...values,
        defaultValue: [enumFieldType.DATE].includes(values?.fieldType)
          ? dayjs(values?.defaultValue).format()
          : values?.defaultValue,
        isSelected: true,
        isOptional: !values?.isMandatory,
        fieldCategory: "custom",
      });
      onClose();
    }
  };

  return (
    <>
      <Button
        className="secondary-btn"
        onClick={() => {
          if (customFieldsLength > 6) {
            setLimitExceed(true);
          } else {
            showDrawer();
          }
        }}
      >
        <i className="fa fa-plus" aria-hidden="true"></i> Add Custom Field
      </Button>
      <Modal
        open={isLimitExceed}
        centered
        width={400}
        footer={[
          <Button
            key="ok"
            type="primary"
            className="theme-btn"
            onClick={() => handleCancel()}
          >
            OK
          </Button>,
        ]}
        onOk={handleCancel}
        onCancel={handleCancel}
      >
        A maximum of 7 custom fields can be created.
      </Modal>
      <Drawer
        zIndex={10000}
        closeIcon={false}
        title={
          <div className="d-flex justify-content-between">
            <div className="heading24px roboto-medium text-color-272727">
              {initialValues?.fieldName ? "Edit" : "Create"} Custom Field
            </div>
            <CloseOutlined style={{ color: "#393939" }} onClick={onClose} />
          </div>
        }
        onClose={onClose}
        open={open}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <div className="web-to-lead">
            <div className="d-flex flex-column w-100">
              <Form.Item
                name="fieldType"
                label={
                  <label
                    className="form-label heading15px"
                    style={{ marginBottom: 3 }}
                  >
                    Field Type
                  </label>
                }
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: "Please select a field type" },
                ]}
              >
                <Select
                  className="leadBtn roboto-medium"
                  placeholder="Select Field Type"
                  options={fieldTypes}
                  onChange={(value) => {
                    form.setFieldsValue({
                      fieldType: value,
                      defaultValue: undefined,
                      pickValues: undefined,
                      fieldLength: undefined,
                    });
                    setType(value);
                  }}
                />
              </Form.Item>
            </div>

            <div className="d-flex flex-column w-100 mt30">
              <Form.Item
                name="fieldName"
                label={
                  <label className="form-label heading15px">Label Name</label>
                }
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: "Label Name is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (
                        existingFieldNames.some(
                          (name) =>
                            name.toLowerCase() === value.trim().toLowerCase()
                        )
                      ) {
                        return Promise.reject("Label Name must be unique");
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input className="common-input" />
              </Form.Item>
            </div>
            {[enumFieldType.TEXT, enumFieldType.TEXT_AREA].includes(type) && (
              <div className="d-flex flex-column w-100 mt30">
                <Form.Item
                  name="fieldLength"
                  style={{ marginBottom: 0 }}
                  label={
                    <label className="form-label heading15px">
                      Field Length
                    </label>
                  }
                >
                  <Input className="common-input" />
                </Form.Item>
              </div>
            )}
            {[enumFieldType.PICK_LIST, enumFieldType.MULTI_PICK_LIST].includes(
              type
            ) && (
              <div className="d-flex flex-column w-100 mt30">
                <PickList form={form} error={error} setError={setError} />
                {error && (
                  <div className="pb-2" style={{ color: "#ff4d4f" }}>
                    {error}
                  </div>
                )}
              </div>
            )}
            {![enumFieldType.PICK_LIST, enumFieldType.MULTI_PICK_LIST].includes(
              type
            ) && (
              <div className="d-flex flex-column w-100 mt30">
                <Form.Item
                  name="defaultValue"
                  style={{ marginBottom: 0 }}
                  label={
                    <label className="form-label heading15px">
                      Default Value
                    </label>
                  }
                >
                  {[enumFieldType.DATE].includes(type) ? (
                    <DatePicker
                      style={{ width: "100%" }}
                      className="common-input"
                    />
                  ) : (
                    <Input
                      type={
                        [enumFieldType.INTEGER].includes(type) ? "number" : ""
                      }
                      min={1}
                      className="common-input"
                    />
                  )}
                </Form.Item>
              </div>
            )}
            <div className="text-right">
              <Form.Item
                name="isMandatory"
                style={{ marginBottom: 0 }}
                valuePropName="checked"
              >
                <Checkbox
                  className="heading15px mt20"
                  style={{ color: "#414141" }}
                >
                  Mandatory Field
                </Checkbox>
              </Form.Item>
            </div>
            <div
              className="mt30 d-flex justify-content-end align-items-center"
              style={{ gap: 20 }}
            >
              <Button className="secondary-btn" onClick={onClose}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" className="theme-btn">
                {initialValues?.fieldName ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </Form>
      </Drawer>
    </>
  );
};

export default CreateEditCustomField;
